import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingBag, Clock, CheckCircle, ArrowRight, Shield, FileText, Plus } from 'lucide-react';

const STATUS_COLORS = {
  order_received: { bg: 'rgba(163,106,58,0.10)', color: '#8A562B' },
  in_review: { bg: 'rgba(31,94,122,0.10)', color: '#1F5E7A' },
  authentic: { bg: 'rgba(14,107,93,0.10)', color: '#0E6B5D' },
  counterfeit: { bg: 'rgba(90,31,42,0.10)', color: '#5A1F2A' },
  appraisal_complete: { bg: 'rgba(14,107,93,0.10)', color: '#0E6B5D' },
  refunded: { bg: 'rgba(90,31,42,0.10)', color: '#5A1F2A' },
};

const STATUS_LABELS = {
  order_received: 'Order Received',
  in_review: 'In Review',
  more_images_requested: 'More Images Requested',
  images_received: 'Images Received',
  authentic: 'Authentic',
  counterfeit: 'Counterfeit',
  appraisal_complete: 'Appraisal Complete',
  unable_to_determine: 'Unable to Determine',
  refunded: 'Refunded',
  revoked: 'Revoked',
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/orders')
      .then(r => setOrders(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const paidOrders = orders.filter(o => o.status !== 'pending' && o.status !== 'cancelled');
  const inProgress = paidOrders.filter(o => o.status === 'paid' || o.status === 'processing');
  const completed = paidOrders.filter(o => o.status === 'completed');

  return (
    <div>
      <h1 className="font-display text-2xl font-medium text-ink mb-1" data-testid="account-dashboard-title">Welcome Back</h1>
      <p className="text-sm text-muted-foreground mb-6">Manage your authentication and appraisal orders.</p>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(163,106,58,0.10)' }}>
              <ShoppingBag className="h-4 w-4" style={{ color: '#A36A3A' }} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Orders</p>
              <p className="text-lg font-semibold text-ink">{paidOrders.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(31,94,122,0.10)' }}>
              <Clock className="h-4 w-4" style={{ color: '#1F5E7A' }} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">In Progress</p>
              <p className="text-lg font-semibold text-ink">{inProgress.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(14,107,93,0.10)' }}>
              <CheckCircle className="h-4 w-4" style={{ color: '#0E6B5D' }} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Completed</p>
              <p className="text-lg font-semibold text-ink">{completed.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent orders */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Recent Orders</h2>
        {paidOrders.length > 0 && (
          <Link to="/account/orders">
            <Button variant="ghost" size="sm" className="text-xs gap-1">View All <ArrowRight className="h-3 w-3" /></Button>
          </Link>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1,2].map(i => <div key={i} className="h-20 bg-stone/20 rounded-lg" />)}
        </div>
      ) : paidOrders.length === 0 ? (
        <Card className="bg-white border-stone/50">
          <CardContent className="p-8 text-center">
            <ShoppingBag className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground mb-4">No orders yet. Start by authenticating or appraising your luxury items.</p>
            <Link to="/order/submit">
              <Button className="gap-2 rounded-[12px]">
                <Plus className="h-4 w-4" /> Start an Order
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {paidOrders.slice(0, 5).map(order => (
            <Link key={order.id} to={`/account/orders/${order.id}`} className="block no-underline">
              <Card className="bg-white border-stone/50 hover:shadow-[var(--shadow-pop)] transition-shadow duration-200">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <span className="font-mono text-xs text-muted-foreground" data-font="mono">{order.order_number}</span>
                    <p className="text-sm font-medium text-ink mt-0.5">{order.items_count} item{order.items_count !== 1 ? 's' : ''}</p>
                    <p className="text-xs text-muted-foreground">${(order.total_cents / 100).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]" style={STATUS_COLORS[order.status]}>
                      {order.status === 'paid' ? 'Order Received' : order.status}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
